import { useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../components/ui/Card';
import { DataNotes } from '../../../components/ui/InfoTip';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { useInvoicesSummary } from '../../../hooks/useFinance';
import type { OverdueInvoice } from '../../../api/finance';
import { CHART_SERIES } from '../../../components/ui/chart';
import { money, formatShortDate } from './formatters';
import { DataIssues, PanelTitle, useFinanceIssues } from './DataIssues';

function dueLabel(inv: OverdueInvoice) {
  if (inv.daysOverdue > 0) {
    return <span className="text-body-xs text-error">{inv.daysOverdue}d overdue</span>;
  }
  const daysLeft = -inv.daysOverdue;
  return (
    <span className="text-body-xs text-text-tertiary">
      {daysLeft === 0 ? 'due today' : `due in ${daysLeft}d`}
    </span>
  );
}

function InvoiceList({ invoices }: { invoices: OverdueInvoice[] }) {
  return (
    <ul className="divide-y divide-border-light">
      {invoices.map((inv) => (
        <li key={inv.invoiceNumber} className="flex items-center justify-between gap-3 py-2.5">
          <div className="min-w-0">
            <p className="text-body-sm text-text-primary truncate">{inv.companyName}</p>
            <p className="text-body-xs text-text-tertiary truncate">
              {inv.invoiceNumber} · due {formatShortDate(inv.dueDate)}
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <span className="text-body-sm text-text-primary font-medium tabular-nums">{money(inv.total, inv.currency)}</span>
            {dueLabel(inv)}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Open invoices by age, from Odoo's open balances. One strip of buckets; click
 * a bucket for the invoices in it. Who is overdue as a customer is the table
 * below it, so this card does not list overdue invoices a second time.
 */
export function ReceivablesAging() {
  const { data, isLoading, error, refetch } = useInvoicesSummary('all');
  const [openBucket, setOpenBucket] = useState<string | null>(null);
  const s = data?.summary;
  useFinanceIssues('customers.receivables', s?.issues);

  if (error) return <ErrorState title="Couldn’t load receivables" error={error} onRetry={() => refetch()} />;
  if (isLoading || !s) {
    return (
      <Card padding="md">
        <LoadingState rows={2} />
      </Card>
    );
  }

  const maxBucket = Math.max(1, ...s.aging.map((b) => b.amount));
  const open = s.aging.find((b) => b.label === openBucket);
  const unknownNote =
    s.unknownResidualCount > 0
      ? [
          `${s.unknownResidualCount} invoice${s.unknownResidualCount === 1 ? '' : 's'} worth ${money(s.unknownResidualInvoiced, s.currency)} ${s.unknownResidualCount === 1 ? 'has' : 'have'} no open balance synced from Odoo, so ${s.unknownResidualCount === 1 ? 'it is' : 'they are'} left out of these buckets.`,
        ]
      : [];
  const scheduled = s.scheduledInvoices;

  return (
    <Card padding="md">
      <PanelTitle
        title="Receivables aging"
        info="Open customer invoices grouped by how far past their due date they are. Click a bucket for its invoices."
        action={<DataNotes notes={unknownNote} />}
      />
      <DataIssues issues={s.issues} />
      {scheduled.length > 0 && (
        // Booked in Odoo but dated after today: not owed, so not in any bucket.
        <p className="mb-3 text-body-xs text-text-tertiary">
          Scheduled, not counted: {money(s.scheduledTotal, s.currency)} on {scheduled.length} invoice
          {scheduled.length === 1 ? '' : 's'} dated after today (
          {scheduled.map((i) => `${i.companyName} ${i.invoiceNumber}, ${formatShortDate(i.issueDate)}`).join('; ')}).
        </p>
      )}
      {s.aging.every((b) => b.count === 0) ? (
        <p className="text-body-sm text-text-tertiary">No outstanding invoices.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {s.aging.map((b) => (
            <button
              key={b.label}
              type="button"
              disabled={b.count === 0}
              onClick={() => setOpenBucket((cur) => (cur === b.label ? null : b.label))}
              className={clsx(
                'rounded-lg p-3 text-left transition-colors disabled:cursor-default',
                openBucket === b.label ? 'bg-surface-secondary' : 'hover:bg-surface-secondary/60'
              )}
            >
              <p className="text-body-xs text-text-tertiary">{b.label}</p>
              {b.count === 0 ? (
                // An empty bucket keeps its place in the strip but says so quietly: no €0, no bar.
                <p className="mt-1 text-body-sm text-text-tertiary">None open</p>
              ) : (
                <>
                  <p className="mt-1 text-body-md font-semibold text-text-primary tabular-nums">{money(b.amount, s.currency)}</p>
                  <p className="text-body-xs text-text-tertiary">{b.count} {b.count === 1 ? 'invoice' : 'invoices'}</p>
                  <div className="mt-2 h-1 w-full rounded-full bg-surface-tertiary overflow-hidden">
                    <div className="h-full" style={{ width: `${(b.amount / maxBucket) * 100}%`, backgroundColor: CHART_SERIES[0] }} />
                  </div>
                </>
              )}
            </button>
          ))}
        </div>
      )}
      {open && open.invoices.length > 0 && (
        <div className="mt-4 border-t border-border-light pt-2">
          <InvoiceList invoices={open.invoices} />
        </div>
      )}
    </Card>
  );
}
